/**
 * DataTable with the filter bar ON TOP (boss rule): search, filter chips, saved
 * filters, sort, column toggle, CSV export — then the table, then paging.
 *
 *   const t = useTableState(['status', 'category']);
 *   const q = useQuery({ queryKey: [org, 'users', t.queryString], queryFn: () => api<Page<User>>(EP.users, { query: toApiQuery(t.state) }) });
 *   <DataTable id="items" table={t} columns={cols} rows={q.data?.data ?? []} total={q.data?.total} filters={[...]} loading={q.isLoading} />
 *
 * Below 640 px rows become stacked cards (first column as title), so there is no
 * sideways page scroll on phones.
 */
import { ArrowDown, ArrowUp, ArrowUpDown, Bookmark, ChevronDown, ChevronLeft, ChevronRight, Columns3, Download, Plus, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { toCsv, downloadText, type CsvColumn } from '../../lib/csv';
import { count as fmtCount, manilaDay } from '../../lib/format';
import { Button, ErrorNote, cx } from './core';
import { EmptyState } from './display';
import { SearchInput } from './form';
import { MenuItem, Popover } from './popover';
import { PAGE_SIZES, type TableState, type useTableState } from './table-state';

export interface Column<T> {
  id: string;
  header: string;
  cell: (row: T) => ReactNode;
  /** Plain value for CSV; columns without it are left out of the export. */
  csv?: (row: T) => string | number | null | undefined;
  sortable?: boolean;
  align?: 'left' | 'right';
  /** Tailwind width class, e.g. 'w-40'. */
  width?: string;
  /** Can the user hide it? (default true; the first column never hides). */
  hideable?: boolean;
  defaultHidden?: boolean;
  /** Hide on the phone card layout. */
  hideOnPhone?: boolean;
}

export interface FilterDef {
  id: string;
  label: string;
  options: { value: string; label: string }[];
  /** Single choice (radio) instead of multi. */
  single?: boolean;
}

interface Props<T> {
  /** Stable id: keys saved filters and column choices in this browser. */
  id: string;
  table: ReturnType<typeof useTableState>;
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** Server total for paging; omit when rows are already the full set. */
  total?: number;
  filters?: FilterDef[];
  searchPlaceholder?: string;
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  onRowClick?: (row: T) => void;
  /** Replaces the default (export current rows). Use for server-side full exports. */
  onExport?: (state: TableState) => void;
  exportName?: string;
  empty?: ReactNode;
  /** Extra controls at the right of the filter bar (rare — primary actions go in the page header). */
  toolbar?: ReactNode;
  /** Keep saved filters on the server (per user) instead of this browser. */
  savedStore?: SavedStore;
}

export interface SavedStore {
  list: { name: string; query: string }[];
  save: (name: string, query: string) => void;
  remove: (name: string) => void;
}

interface Saved {
  name: string;
  query: string;
}

const store = {
  get<V>(k: string, d: V): V {
    try {
      const v = localStorage.getItem(k);
      return v ? (JSON.parse(v) as V) : d;
    } catch {
      return d;
    }
  },
  set(k: string, v: unknown) {
    try {
      localStorage.setItem(k, JSON.stringify(v));
    } catch {
      /* private mode */
    }
  },
};

export function DataTable<T>({ id, table, columns, rows, rowKey, total, filters = [], searchPlaceholder = 'Search', loading, error, onRetry, onRowClick, onExport, exportName, empty, toolbar, savedStore }: Props<T>) {
  const { state, set } = table;
  const [qDraft, setQDraft] = useState(state.q);
  const [hidden, setHidden] = useState<string[]>(() => store.get(`wms.cols.${id}`, columns.filter((c) => c.defaultHidden).map((c) => c.id)));
  const [localSaved, setSaved] = useState<Saved[]>(() => store.get(`wms.saved.${id}`, []));
  const saved = savedStore?.list ?? localSaved;
  const [naming, setNaming] = useState<string | null>(null);

  // Debounced search into the URL.
  useEffect(() => setQDraft(state.q), [state.q]);
  useEffect(() => {
    if (qDraft === state.q) return;
    const t = setTimeout(() => set({ q: qDraft }), 300);
    return () => clearTimeout(t);
  }, [qDraft, state.q, set]);

  const visible = useMemo(() => columns.filter((c, i) => i === 0 || !hidden.includes(c.id)), [columns, hidden]);
  const totalRows = total ?? rows.length;
  const pages = Math.max(1, Math.ceil(totalRows / state.pageSize));
  const activeFilters = filters.filter((f) => state.filters[f.id]?.length);
  const anyFilter = !!state.q || activeFilters.length > 0;

  const toggleCol = (cid: string) => {
    const next = hidden.includes(cid) ? hidden.filter((h) => h !== cid) : [...hidden, cid];
    setHidden(next);
    store.set(`wms.cols.${id}`, next);
  };
  const setFilter = (fid: string, values: string[]) => set({ filters: { ...state.filters, [fid]: values } });
  const clearAll = () => set({ q: '', filters: Object.fromEntries(filters.map((f) => [f.id, []])) });
  const applySaved = (s: Saved) => {
    const p = new URLSearchParams(s.query);
    set({
      q: p.get('q') ?? '',
      filters: Object.fromEntries(filters.map((f) => [f.id, p.get(f.id)?.split(',').filter(Boolean) ?? []])),
      sort: p.get('sort') ? { id: p.get('sort')!.replace(/^-/, ''), dir: p.get('sort')!.startsWith('-') ? 'desc' : 'asc' } : state.sort,
    });
  };
  const saveCurrent = (name: string) => {
    const p = new URLSearchParams();
    if (state.q) p.set('q', state.q);
    for (const f of filters) if (state.filters[f.id]?.length) p.set(f.id, state.filters[f.id].join(','));
    if (state.sort) p.set('sort', `${state.sort.dir === 'desc' ? '-' : ''}${state.sort.id}`);
    if (savedStore) return savedStore.save(name, p.toString());
    const next = [...saved.filter((s) => s.name !== name), { name, query: p.toString() }];
    setSaved(next);
    store.set(`wms.saved.${id}`, next);
  };
  const removeSaved = (name: string) => {
    if (savedStore) return savedStore.remove(name);
    const next = saved.filter((s) => s.name !== name);
    setSaved(next);
    store.set(`wms.saved.${id}`, next);
  };
  const sortBy = (c: Column<T>) => {
    if (!c.sortable) return;
    const cur = state.sort?.id === c.id ? state.sort.dir : null;
    set({ sort: cur === null ? { id: c.id, dir: 'asc' } : cur === 'asc' ? { id: c.id, dir: 'desc' } : null }, false);
  };
  const exportCsv = () => {
    if (onExport) return onExport(state);
    const cols: CsvColumn<T>[] = visible.filter((c) => c.csv).map((c) => ({ header: c.header, value: c.csv! }));
    downloadText(`${exportName ?? id}-${manilaDay()}.csv`, toCsv(rows, cols));
  };
  const canExport = !!onExport || columns.some((c) => c.csv);

  return (
    <div className="rounded-panel border border-line bg-card shadow-panel">
      {/* Filter bar — always on top */}
      <div className="flex flex-col gap-2 border-b border-line p-3">
        <div className="flex flex-wrap items-center gap-2">
          <SearchInput label={searchPlaceholder} placeholder={searchPlaceholder} value={qDraft} onChange={setQDraft} className="min-w-[200px] flex-1 sm:max-w-sm" />
          {filters.map((f) => (
            <FilterChip key={f.id} def={f} values={state.filters[f.id] ?? []} onChange={(v) => setFilter(f.id, v)} />
          ))}
          <div className="ml-auto flex items-center gap-1">
            {toolbar}
            <Popover
              align="right"
              label="Saved filters"
              trigger={({ open, toggle, id: pid }) => (
                <Button variant="quiet" size="md" aria-haspopup="menu" aria-expanded={open} aria-controls={pid} onClick={toggle} icon={<Bookmark aria-hidden className="h-4 w-4" />}>
                  <span className="hidden lg:inline">Saved</span>
                </Button>
              )}
            >
              {(close) => (
                <div className="w-64">
                  {saved.length === 0 && <p className="px-2.5 py-2 text-sm text-muted">No saved filters yet.</p>}
                  {saved.map((s) => (
                    <div key={s.name} className="flex items-center">
                      <MenuItem
                        onClick={() => {
                          applySaved(s);
                          close();
                        }}
                      >
                        {s.name}
                      </MenuItem>
                      <button type="button" aria-label={`Delete saved filter ${s.name}`} onClick={() => removeSaved(s.name)} className="grid h-11 w-10 shrink-0 place-items-center text-muted hover:text-danger">
                        <Trash2 aria-hidden className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <div className="mt-1 border-t border-line pt-1">
                    {naming === null ? (
                      <MenuItem icon={<Plus />} onClick={() => setNaming('')}>
                        Save current filters
                      </MenuItem>
                    ) : (
                      <form
                        className="flex gap-1 p-1"
                        onSubmit={(e) => {
                          e.preventDefault();
                          if (naming.trim()) saveCurrent(naming.trim());
                          setNaming(null);
                          close();
                        }}
                      >
                        <input autoFocus aria-label="Name for this filter" placeholder="Name" value={naming} onChange={(e) => setNaming(e.target.value)} className="min-h-10 min-w-0 flex-1 rounded-[4px] border border-line bg-card px-2 text-sm" />
                        <Button size="sm" type="submit">
                          Save
                        </Button>
                      </form>
                    )}
                  </div>
                </div>
              )}
            </Popover>
            <Popover
              align="right"
              label="Columns"
              trigger={({ open, toggle, id: pid }) => (
                <Button variant="quiet" aria-haspopup="menu" aria-expanded={open} aria-controls={pid} onClick={toggle} icon={<Columns3 aria-hidden className="h-4 w-4" />}>
                  <span className="hidden lg:inline">Columns</span>
                </Button>
              )}
            >
              {() =>
                columns.slice(1).filter((c) => c.hideable !== false).map((c) => (
                  <MenuItem key={c.id} checked={!hidden.includes(c.id)} onClick={() => toggleCol(c.id)}>
                    {c.header}
                  </MenuItem>
                ))
              }
            </Popover>
            {canExport && (
              <Button variant="quiet" onClick={exportCsv} disabled={!rows.length} icon={<Download aria-hidden className="h-4 w-4" />}>
                <span className="hidden lg:inline">Export CSV</span>
                <span className="sr-only lg:hidden">Export CSV</span>
              </Button>
            )}
          </div>
        </div>
        {anyFilter && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted">
              {fmtCount(totalRows)} {totalRows === 1 ? 'result' : 'results'}
            </span>
            <button type="button" onClick={clearAll} className="min-h-9 font-semibold text-brand underline-offset-2 hover:underline">
              Clear filters
            </button>
          </div>
        )}
      </div>

      <ErrorNote error={error} onRetry={onRetry} />

      {/* Desktop table */}
      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-line bg-sunken text-left">
              {visible.map((c) => {
                const dir = state.sort?.id === c.id ? state.sort.dir : null;
                return (
                  <th key={c.id} scope="col" aria-sort={dir ? (dir === 'asc' ? 'ascending' : 'descending') : undefined} className={cx('h-10 whitespace-nowrap px-3 font-semibold text-muted first:pl-4 last:pr-4', c.align === 'right' && 'text-right', c.width)}>
                    {c.sortable ? (
                      <button type="button" onClick={() => sortBy(c)} className={cx('inline-flex min-h-9 items-center gap-1 hover:text-ink', c.align === 'right' && 'flex-row-reverse', dir && 'text-ink')}>
                        {c.header}
                        {dir === 'asc' ? <ArrowUp aria-hidden className="h-3.5 w-3.5" /> : dir === 'desc' ? <ArrowDown aria-hidden className="h-3.5 w-3.5" /> : <ArrowUpDown aria-hidden className="h-3.5 w-3.5 opacity-40" />}
                      </button>
                    ) : (
                      c.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading &&
              Array.from({ length: 6 }, (_, i) => (
                <tr key={`sk${i}`} className="border-b border-line last:border-0">
                  {visible.map((c) => (
                    <td key={c.id} className="h-12 px-3 first:pl-4">
                      <span className="block h-3.5 w-3/4 animate-pulse rounded bg-sunken" />
                    </td>
                  ))}
                </tr>
              ))}
            {!loading &&
              rows.map((r) => (
                <tr
                  key={rowKey(r)}
                  onClick={onRowClick ? () => onRowClick(r) : undefined}
                  onKeyDown={onRowClick ? (e) => e.key === 'Enter' && onRowClick(r) : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  className={cx('border-b border-line last:border-0', onRowClick && 'cursor-pointer hover:bg-sunken focus-visible:bg-sunken')}
                >
                  {visible.map((c) => (
                    <td key={c.id} className={cx('h-12 px-3 py-2 align-middle first:pl-4 last:pr-4', c.align === 'right' && 'money text-right')}>
                      {c.cell(r)}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Phone cards */}
      <ul className="ledger sm:hidden">
        {loading && <li className="p-4 text-sm text-muted">Loading…</li>}
        {!loading &&
          rows.map((r) => {
            const [first, ...rest] = visible.filter((c) => !c.hideOnPhone);
            const body = (
              <>
                <div className="font-semibold">{first?.cell(r)}</div>
                <dl className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
                  {rest.slice(0, 6).map((c) => (
                    <div key={c.id} className="min-w-0">
                      <dt className="text-xs text-muted">{c.header}</dt>
                      <dd className={cx('truncate', c.align === 'right' && 'money')}>{c.cell(r)}</dd>
                    </div>
                  ))}
                </dl>
              </>
            );
            return (
              <li key={rowKey(r)}>
                {onRowClick ? (
                  <button type="button" onClick={() => onRowClick(r)} className="block w-full px-4 py-3 text-left active:bg-sunken">
                    {body}
                  </button>
                ) : (
                  <div className="px-4 py-3">{body}</div>
                )}
              </li>
            );
          })}
      </ul>

      {!loading && !rows.length && !error && (
        <div className="border-t border-line">
          {anyFilter ? (
            <EmptyState compact title="Nothing matches these filters" body="Try fewer filters or a different search." action={<Button variant="secondary" onClick={clearAll}>Clear filters</Button>} />
          ) : (
            empty ?? <EmptyState compact title="Nothing here yet" />
          )}
        </div>
      )}

      {totalRows > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-3 py-2 text-sm text-muted">
          <span className="tabular">
            {fmtCount((state.page - 1) * state.pageSize + 1)}–{fmtCount(Math.min(state.page * state.pageSize, totalRows))} of {fmtCount(totalRows)}
          </span>
          <div className="flex items-center gap-1">
            <label className="mr-2 hidden items-center gap-2 sm:flex">
              Rows
              <select value={state.pageSize} onChange={(e) => set({ pageSize: Number(e.target.value) })} className="min-h-9 rounded-[4px] border border-line bg-card px-2 text-ink">
                {PAGE_SIZES.map((n) => (
                  <option key={n}>{n}</option>
                ))}
              </select>
            </label>
            <Button variant="quiet" aria-label="Previous page" disabled={state.page <= 1} onClick={() => set({ page: state.page - 1 }, false)}>
              <ChevronLeft aria-hidden className="h-4 w-4" />
            </Button>
            <span className="tabular px-1">
              {state.page} / {pages}
            </span>
            <Button variant="quiet" aria-label="Next page" disabled={state.page >= pages} onClick={() => set({ page: state.page + 1 }, false)}>
              <ChevronRight aria-hidden className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function FilterChip({ def, values, onChange }: { def: FilterDef; values: string[]; onChange: (v: string[]) => void }) {
  const active = values.length > 0;
  const summary = !active ? def.label : values.length === 1 ? `${def.label}: ${def.options.find((o) => o.value === values[0])?.label ?? values[0]}` : `${def.label}: ${values.length}`;
  return (
    <div className="flex items-center">
      <Popover
        label={def.label}
        trigger={({ open, toggle, id }) => (
          <button
            type="button"
            aria-haspopup="menu"
            aria-expanded={open}
            aria-controls={id}
            onClick={toggle}
            className={cx(
              'inline-flex min-h-touch items-center gap-1.5 rounded-full border px-3.5 text-sm font-medium transition-colors',
              active ? 'border-brand bg-brand-soft text-brand' : 'border-dashed border-strong text-muted hover:border-solid hover:text-ink',
              active && 'rounded-r-none border-r-0 pr-2',
            )}
          >
            {!active && <Plus aria-hidden className="h-3.5 w-3.5" />}
            {summary}
            <ChevronDown aria-hidden className="h-3.5 w-3.5" />
          </button>
        )}
      >
        {(close) => (
          <div className="max-h-80 overflow-auto">
            {def.options.map((o) => (
              <MenuItem
                key={o.value}
                checked={values.includes(o.value)}
                onClick={() => {
                  if (def.single) {
                    onChange(values.includes(o.value) ? [] : [o.value]);
                    close();
                  } else onChange(values.includes(o.value) ? values.filter((v) => v !== o.value) : [...values, o.value]);
                }}
              >
                {o.label}
              </MenuItem>
            ))}
          </div>
        )}
      </Popover>
      {active && (
        <button type="button" aria-label={`Remove ${def.label} filter`} onClick={() => onChange([])} className="grid min-h-touch w-9 place-items-center rounded-r-full border border-l-0 border-brand bg-brand-soft text-brand hover:bg-brand/15">
          <X aria-hidden className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
